import { DateTime } from "luxon";

// ============================================================
//  Time-zone rules the app carries with it.
//
//  Browsers ship their own copy of the IANA time-zone database, and an
//  older copy gives old answers: a browser built before tzdb 2026c
//  still puts Morocco on UTC+1 after its move to permanent UTC+0 on
//  2026-09-20. public/data/tz-rules.json (scripts/build-tz-rules.py,
//  from the current tzdb) lists every offset change of every zone the
//  app shows, so all visitors see the same, current times. A zone or a
//  moment outside the table falls back to the browser (luxon).
// ============================================================

const DATA_URL = "/data/tz-rules.json";

/** zone → [[utcMillis, offsetMinutes], …], oldest first. */
let table: Map<string, [number, number][]> | null = null;
let tableEnd = 0;

export async function loadTzRules(): Promise<void> {
  try {
    const res = await fetch(DATA_URL);
    if (!res.ok) throw new Error(`tz-rules: ${res.status}`);
    const data = (await res.json()) as { to: string; zones: Record<string, [number, number][]> };
    table = new Map(Object.entries(data.zones));
    tableEnd = Date.parse(data.to);
  } catch (err) {
    console.error("tz rules unavailable, using the browser's", err);
  }
}

/** UTC offset of `zone` at `ms`, in minutes east of UTC. */
export function offsetAt(zone: string, ms: number): number {
  const changes = table?.get(zone);
  if (changes && ms >= changes[0][0] && ms < tableEnd) {
    let off = changes[0][1];
    for (const [t, o] of changes) {
      if (t > ms) break;
      off = o;
    }
    return off;
  }
  return DateTime.fromMillis(ms).setZone(zone).offset;
}

/** The moment `now` as wall-clock time in `zone`, by the table's offset.
 *  The result is a fixed-offset DateTime: format it, don't rezone it. */
export function inZone(now: DateTime, zone: string): DateTime {
  return now.setZone(`UTC${formatShift(offsetAt(zone, now.toMillis()))}`);
}

// 330 → "+5:30", −300 → "-5", 0 → "+0" (luxon's fixed-offset zone names).
function formatShift(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  const m = abs % 60;
  return `${sign}${Math.floor(abs / 60)}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}
